import React, { useState } from 'react';
import { 
  CalendarOutlined, 
  UserOutlined, 
  ClockCircleOutlined,
  TagOutlined,
  HistoryOutlined,
  DatabaseOutlined,
  DownOutlined,
  RightOutlined
} from '@ant-design/icons';
import { CardSectionProps } from '../../../types/compositeCards';

export const CardMetadata: React.FC<CardSectionProps> = ({
  section,
  card,
  onAction
}) => {
  const { metadata, context, data } = card;
  const { uiRole, modelType, dataSource } = context;
  
  // Check if metadata should be collapsed by default
  const defaultCollapsed = section.props?.defaultCollapsed ?? false;
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getMetadataItems = () => {
    const items = [];

    // Basic metadata
    items.push({
      icon: <CalendarOutlined />,
      label: 'Created',
      value: formatDate(metadata.createdAt),
      color: '#6b7280'
    });

    items.push({
      icon: <UserOutlined />,
      label: 'Created By',
      value: metadata.createdBy,
      color: '#6b7280'
    });

    if (metadata.updatedAt !== metadata.createdAt) {
      items.push({
        icon: <ClockCircleOutlined />,
        label: 'Updated',
        value: formatDate(metadata.updatedAt),
        color: '#6b7280'
      });
    }

    // Command history
    if (metadata.commandHistory) {
      items.push({
        icon: <HistoryOutlined />,
        label: 'Command',
        value: `/${metadata.commandHistory.command}`,
        color: '#3b82f6'
      });

      if (metadata.commandHistory.executionTime) {
        items.push({
          icon: <ClockCircleOutlined />,
          label: 'Execution Time',
          value: `${metadata.commandHistory.executionTime}ms`,
          color: '#10b981'
        });
      }

      if (metadata.commandHistory.dataSource) {
        items.push({
          icon: <DatabaseOutlined />,
          label: 'Data Source',
          value: metadata.commandHistory.dataSource,
          color: '#8b5cf6'
        });
      }
    }

    // Tags
    if (metadata.tags && metadata.tags.length > 0) {
      items.push({
        icon: <TagOutlined />,
        label: 'Tags',
        value: metadata.tags.join(', '),
        color: '#f59e0b'
      });
    }

    // Version
    items.push({
      icon: <TagOutlined />,
      label: 'Version',
      value: `v${metadata.version}`,
      color: '#6b7280'
    });

    return items;
  };

  const metadataItems = getMetadataItems();

  return (
    <div style={{
      padding: '12px',
      backgroundColor: '#f9fafb',
      borderRadius: '8px',
      borderTop: '1px solid #e5e7eb'
    }}>
      <h4 
        onClick={() => setIsCollapsed(!isCollapsed)}
        style={{ 
          fontSize: '14px', 
          fontWeight: '500', 
          margin: '0 0 12px 0', 
          color: '#374151',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          cursor: 'pointer',
          userSelect: 'none'
        }}
      >
        {isCollapsed ? 
          <RightOutlined style={{ fontSize: '12px' }} /> : 
          <DownOutlined style={{ fontSize: '12px' }} />
        }
        <TagOutlined style={{ fontSize: '14px' }} />
        Metadata
      </h4>
      
      {/* Collapsible Content */}
      {!isCollapsed && (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {metadataItems.map((item, index) => (
              <div key={index} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 8px',
                backgroundColor: 'white',
                borderRadius: '4px',
                fontSize: '12px'
              }}>
                <div style={{ color: item.color, display: 'flex', alignItems: 'center' }}>
                  {item.icon}
                </div>
                <span style={{ color: '#6b7280', fontWeight: '500', minWidth: '80px' }}>
                  {item.label}:
                </span>
                <span style={{ color: '#374151', flex: 1 }}>
                  {item.value}
                </span>
              </div>
            ))}
          </div>

          {/* Context Information */}
          <div style={{
            marginTop: '12px',
            padding: '8px',
            backgroundColor: '#f3f4f6',
            borderRadius: '4px',
            fontSize: '11px',
            color: '#6b7280'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Role: {uiRole}</span>
              <span>Model: {modelType}</span>
              <span>Source: {dataSource}</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
